<template>
  <div v-if="initialLoadComplete && (showAlert || dialog)">
    <v-alert data-cy="billingAndRoiWarning" dense type="info">
      Billing and ROI locations/departments have not been built for this
      organization yet.
      <template v-if="isOchinUser" v-slot:append>
        <v-btn small dark outlined @click="build">Build</v-btn>
      </template>
    </v-alert>
    <v-dialog v-model="dialog" persistent max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline"
            >Building Billing and ROI Locations and Departments</span
          >
        </v-card-title>
        <v-card-text>
          <v-container>
            <div
              v-for="build in buildOrder"
              :key="build.attribute"
              class="mb-3"
            >
              <v-icon v-if="buildStatus[build.attribute] === 'Building...'"
                >fas fa-fw fa-spin fa-spinner</v-icon
              >
              <v-icon v-else-if="buildStatus[build.attribute] === 'Built'"
                >far fa-fw fa-check-square</v-icon
              >
              <v-icon v-else>far fa-fw fa-square</v-icon>
              {{ build.label }}:
              {{ buildStatus[build.attribute] }}
            </div>

            <div class="mt-6">
              <div v-if="errorMessage">
                <v-alert type="warning">{{ errorMessage }}</v-alert>
              </div>
              <div v-if="allSuccesfullyBuilt">
                <v-alert type="success"
                  >Billing and ROI locations and departments successfully
                  built</v-alert
                >
              </div>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            :disabled="buildInProgress"
            text
            @click="reset"
            >{{ buildInProgress ? "Building..." : "OK" }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  computed: {
    hasBilling() {
      return !!this.filterLocationList("isBilling", 1).length;
    },
    hasRoi() {
      return !!this.filterLocationList("isRoi", 1).length;
    },
    showAlert() {
      return !(this.hasBilling && this.hasRoi) && this.serviceAreaId;
    },
    ...mapGetters({
      filterLocationList: "location/filterByAttributeValue",
    }),
    ...mapState({
      isOchinUser: state => state.session.isOchinUser,
      locationList: state => state.location.list,
      organization: state => state.organization.record,
      organizationId: state => state.session.organizationId,
      serviceAreaId: state => state.organization.record.serviceAreaId,
    }),
  },
  async created() {
    try {
      await Promise.all([
        this.getLocationList(),
        this.getOrganizationById(this.organizationId),
      ]);
      this.reset();
      this.initialLoadComplete = true;
    } catch (e) {
      console.error(e);
    }
  },
  data() {
    return {
      allSuccesfullyBuilt: false,
      buildInProgress: false,
      buildOrder: [
        {
          label: "Billing Location",
          attribute: "billingLocation",
        },
        {
          label: "Billing Department",
          attribute: "billingDepartment",
        },
        {
          label: "ROI Location",
          attribute: "roiLocation",
        },
        {
          label: "ROI Department",
          attribute: "roiDepartment",
        },
      ],
      buildStatus: {
        billingDepartment: "Not Built",
        billingLocation: "Not Built",
        roiDepartment: "Not Built",
        roiLocation: "Not Built",
      },
      dialog: false,
      errorMessage: null,
      initialLoadComplete: false,
    };
  },
  methods: {
    async build() {
      this.buildInProgress = true;
      this.dialog = true;

      try {
        if (this.buildStatus.billingLocation === "Not Built") {
          const billingLocation = await this.createBillingLocation();
          await this.createBillingDepartment(billingLocation);
        }

        if (this.buildStatus.roiLocation === "Not Built") {
          const roiLocation = await this.createRoiLocation();
          await this.createRoiDepartment(roiLocation);
        }

        // All is complete
        this.allSuccesfullyBuilt = true;
        this.buildInProgress = false;
      } catch (e) {
        console.error(e);
        this.errorMessage = e.message;
        this.buildInProgress = false;
      }
    },
    async createBillingDepartment({ locationId }) {
      this.buildStatus.billingDepartment = "Building...";
      const billingDepartmentData = {
        departmentNumber: `${this.serviceAreaId}100001`,
        externalName: "Billing",
        locationId,
        name: "Billing",
      };

      let abbreviation = null;
      if (
        this.organization.abbreviation &&
        this.organization.abbreviation.length < 6
      ) {
        abbreviation = `${this.organization.abbreviation.toUpperCase()}BILL`;
        billingDepartmentData.abbreviation = abbreviation;
      }

      const billingDepartment = await this.createDepartment(
        billingDepartmentData
      );
      this.buildStatus.billingDepartment = "Built";
      return billingDepartment;
    },
    async createBillingLocation() {
      this.buildStatus.billingLocation = "Building...";
      const billingLocationData = {
        isBilling: 1,
        locationNumber: `${this.serviceAreaId}100`,
        name: "Billing",
        organizationId: this.organizationId,
      };

      let abbreviation = null;
      if (
        this.organization.abbreviation &&
        this.organization.abbreviation.length < 5
      ) {
        abbreviation = `${this.organization.abbreviation.toUpperCase()}B`;
        billingLocationData.abbreviation = abbreviation;
      }
      const billingLocation = await this.createLocation(billingLocationData);
      this.buildStatus.billingLocation = "Built";
      return billingLocation;
    },
    async createRoiDepartment({ locationId }) {
      this.buildStatus.roiDepartment = "Building...";
      const roiDepartmentData = {
        departmentNumber: `${this.serviceAreaId}10010001001`,
        externalName: "ROI",
        locationId,
        name: "ROI",
      };

      let abbreviation = null;
      if (
        this.organization.abbreviation &&
        this.organization.abbreviation.length < 6
      ) {
        abbreviation = `${this.organization.abbreviation.toUpperCase()}ROI`;
        roiDepartmentData.abbreviation = abbreviation;
      }

      const roiDepartment = await this.createDepartment(roiDepartmentData);
      this.buildStatus.roiDepartment = "Built";
      return roiDepartment;
    },
    async createRoiLocation() {
      this.buildStatus.roiLocation = "Building...";
      const roiLocationData = {
        isRoi: 1,
        locationNumber: `${this.serviceAreaId}10010001001`,
        name: "ROI",
        organizationId: this.organizationId,
      };

      let abbreviation = null;
      if (
        this.organization.abbreviation &&
        this.organization.abbreviation.length < 5
      ) {
        abbreviation = `${this.organization.abbreviation.toUpperCase()}R`;
        roiLocationData.abbreviation = abbreviation;
      }
      const roiLocation = await this.createLocation(roiLocationData);
      this.buildStatus.roiLocation = "Built";
      return roiLocation;
    },

    reset() {
      this.allSuccesfullyBuilt = false;
      this.dialog = false;
      this.buildInProgress = false;
      this.buildStatus.billingDepartment = this.hasBilling
        ? "Built"
        : "Not Built";
      this.buildStatus.billingLocation = this.hasBilling
        ? "Built"
        : "Not Built";
      this.buildStatus.roiDepartment = this.hasRoi ? "Built" : "Not Built";
      this.buildStatus.roiLocation = this.hasRoi ? "Built" : "Not Built";
      this.errorMessage = null;
    },
    ...mapActions({
      createDepartment: "department/create",
      createLocation: "location/create",
      getLocationList: "location/list",
      getOrganizationById: "organization/getByOrganizationId",
    }),
  },
};
</script>
